import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

export default function ScrollToTopOnBack() {
  const lastHistoryIdxRef = useRef<number | null>(null);
  const lastPathRef = useRef<string | null>(null);
  const router = useRouter();

  // Keep track of the last pathname on any route change (including query changes)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      lastPathRef.current = window.location.pathname;
    }

    const handleRouteChangeComplete = (url: string) => {
      try {
        const nextPathname = new URL(url, window.location.origin).pathname;
        lastPathRef.current = nextPathname;
      } catch {
        // Fallback if URL parsing fails; use current pathname
        lastPathRef.current = window.location.pathname;
      }
    };

    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router.events]);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      lastHistoryIdxRef.current = window.history.state?.idx ?? 0;
      lastPathRef.current = window.location.pathname;
    }
    const handlePopState = () => {
      if (typeof window === 'undefined') return;

      const prevIdx = lastHistoryIdxRef.current;
      const rawIdx = window.history.state?.idx;
      const nextIdx = typeof rawIdx === 'number' ? rawIdx : null;
      const prevPath = lastPathRef.current;
      const nextPath = window.location.pathname;

      // Scroll only on back navigation; if index unavailable, assume back to honor request
      const isBack =
        typeof prevIdx === 'number' && typeof nextIdx === 'number' ? nextIdx < prevIdx : true;
      const pathChanged = typeof prevPath === 'string' ? prevPath !== nextPath : true;

      // Do not scroll for query/hash-only changes; require pathname change
      if (isBack && pathChanged) {
        window.scrollTo(0, 0);
      }

      if (typeof nextIdx === 'number') {
        lastHistoryIdxRef.current = nextIdx;
      }
      lastPathRef.current = nextPath;
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  return null;
}
